import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { XCircle, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import { WARRANTY_REJECTION_REASONS } from "@/lib/warrantyUtils";

const RejectClaimForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState("out_of_warranty");
  const [rejectionNotes, setRejectionNotes] = useState("");

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        rejection_reason: rejectionReason,
        rejection_notes: rejectionNotes.trim() || undefined,
      };
      const res = await warrantyApi.reject(claim.id, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Warranty claim rejected");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }

      toast.info("Rejection notice has been auto-issued.", {
        action: {
          label: "Print Notice",
          onClick: () =>
            warrantyApi.openPdfReport(warrantyApi.pdf.rejection(claim.id)),
        },
        duration: 8000,
      });

      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to reject claim";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!rejectionReason) {
      toast.error("Please select a rejection reason");
      return;
    }
    rejectMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-destructive/10 rounded-lg text-destructive">
            <XCircle className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Reject Claim #{claim.claim_number}</DialogTitle>
            <DialogDescription>
              Provide formal reason for warranty denial. A rejection notice will be auto-issued.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="rejection_reason">Rejection Reason *</Label>
          <Select value={rejectionReason} onValueChange={setRejectionReason}>
            <SelectTrigger id="rejection_reason">
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>
            <SelectContent>
              {WARRANTY_REJECTION_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="rejection_notes">
            Rejection Details / Notes{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (Printed on customer rejection notice)
            </span>
          </Label>
          <Textarea
            id="rejection_notes"
            value={rejectionNotes}
            onChange={(e) => setRejectionNotes(e.target.value)}
            placeholder="Provide specific findings (e.g. Liquid damage verified on the mainboard, serial mismatch)..."
            rows={4}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rejectMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="destructive"
            disabled={rejectMutation.isPending}
          >
            {rejectMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rejecting...
              </>
            ) : (
              "Confirm Rejection"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

RejectClaimForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const RejectClaimModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {open && (
          <RejectClaimForm
            key={claim.id || "reject"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

RejectClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default RejectClaimModal;
